import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { CachedOutlined, Search } from "@mui/icons-material"
import PageHeader from "../../components/PageHeader/PageHeader"
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material"
import { CustomerTable } from "./CustomerTable"
import { useLocation, useNavigate } from "react-router-dom"
import queryString from "query-string"
import { GridSortModel } from "@mui/x-data-grid"
import { ROOT } from "../../constants/path"
import { StyledBox, StyledButton } from "../Transactions/styles"
import { GridParent } from "../BankAccounts/styles"
import { useEffect, useMemo } from "react"
import {
  fetchCustomerRepositoryRequest,
  selectCustomerState,
} from "../../slices/customerSlice"
import { debounce } from "lodash"
import { BusinessEntity } from "../../constants/data"
import { getParam } from "../../utils/customerHelpers"
import { CustomerContainer, CustomerFilterWrapper } from "./styles"
import { PaginationType } from "../../models/customer.model"

export const CustomerRepository = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const { customersRepository, loading, pageInfo } =
    useAppSelector(selectCustomerState)

  const params = useMemo(
    () => queryString.parse(location.search),
    [location.search],
  )

  const filters = {
    customer: getParam(params.customer) ?? undefined,
    description: getParam(params.description) ?? undefined,
    source: getParam(params.source) ?? "all",
    sortBy: getParam(params.sortBy),
    orderBy: getParam(params.orderBy),
  }

  const pagination = {
    page: Number(getParam(params.page) ?? 0),
    pageSize: Number(getParam(params.pageSize) ?? 10),
  }

  const updateURL = (next: Record<string, any>) => {
    navigate({
      search: queryString.stringify({
        ...params,
        ...next,
      }),
    })
  }

  const debouncedFetch = useMemo(
    () =>
      debounce((payload) => {
        dispatch(fetchCustomerRepositoryRequest(payload))
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    dispatch(
      fetchCustomerRepositoryRequest({
        ...filters,
        ...pagination,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.source,
    filters.sortBy,
    filters.orderBy,
    pagination.page,
    pagination.pageSize,
  ])

  useEffect(() => {
    debouncedFetch({
      ...filters,
      ...pagination,
      page: pagination.page ?? 0,
    })

    return () => debouncedFetch.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.customer, filters.description])

  const onCustomerSearch = (value: string) =>
    updateURL({ customer: value, page: 0 })

  const onDescriptionSearch = (value: string) =>
    updateURL({ description: value, page: 0 })

  const onSourceChange = (value: string) =>
    updateURL({ source: value, page: 0 })

  const setPaginationModel = (page: PaginationType) =>
    updateURL({ page: page.page, pageSize: page.pageSize })

  const handleSortModelChange = (model: GridSortModel) => {
    if (!model[0]) return
    updateURL({
      sortBy: model[0].field,
      orderBy: model[0].sort,
      page: 0,
    })
  }

  const handleRefresh = () =>
    dispatch(
      fetchCustomerRepositoryRequest({
        ...filters,
        ...pagination,
      }),
    )

  return (
    <GridParent>
      <PageHeader
        title="Customers Repository"
        breadcrums={[
          { title: "Home", path: ROOT },
          { title: "Customers Repository" },
        ]}
      />
      <CustomerContainer sx={{ display: "flex", flexDirection: "row" }}>
        <Stack spacing={2} sx={{ width: "100%" }}>
          <StyledBox display="flex" gap={2}>
            <CustomerFilterWrapper>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by Customers..."
                value={filters.customer}
                onChange={(e) => onCustomerSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by description..."
                value={filters.description}
                onChange={(e) => onDescriptionSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" style={{ minWidth: 120 }}>
                <InputLabel id="select-source-type">Source</InputLabel>
                <Select
                  labelId="select-source-type"
                  id="selectSourceType"
                  label="Type"
                  value={filters.source}
                  onChange={(e: SelectChangeEvent) =>
                    onSourceChange(e.target.value)
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  {Object.values(BusinessEntity).map((entity) => (
                    <MenuItem key={entity} value={entity}>
                      {entity.charAt(0).toUpperCase() +
                        entity.slice(1).toLowerCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CustomerFilterWrapper>
            <div>
              <StyledButton
                variant="outlined"
                startIcon={<CachedOutlined fontSize="small" />}
                color="inherit"
                onClick={handleRefresh}
                sx={{ minWidth: "100px" }}
              >
                <div>Refresh </div>
              </StyledButton>
            </div>
          </StyledBox>

          <CustomerTable
            customers={customersRepository}
            isLoading={loading}
            pageInfo={pageInfo}
            paginationModel={pagination}
            setPaginationModel={setPaginationModel}
            onSortModelChange={handleSortModelChange}
          />
        </Stack>
      </CustomerContainer>
    </GridParent>
  )
}
